import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Input, Button, Space, Segmented, Progress, Tag, Drawer, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faUsers,
  faEnvelope,
  faCommentSms,
  faCircleCheck,
  faCalendar,
  faLocationDot,
  faMagnifyingGlass,
  faChevronRight,
  faClockRotateLeft,
  faPhoneVolume
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../constants/colors';
import caseService from '../../services/CaseService';
import { usePageHeader } from '../../layouts/AdminLayout';
import OutreachMembersTable from './components/OutreachMembersTable';
import OutreachTableSkeleton from './components/OutreachTableSkeleton';
import EventOutreachDrawer from './components/EventOutreachDrawer';
import ComposeOutreachModal from './components/ComposeOutreachModal';
import PatientEmailPanel from '../../components/PatientEmailPanel';
import EmailLogTable from './components/EmailLogTable';
import CallQueueTab from './components/CallQueueTab';
import {
  PageContainer,
  StatsRow,
  OutreachStatCard,
  FilterBar,
  CaseTable,
  StyledTabs,
  TabsBorderDiv,
  TabBadge,
  OutreachHeartLoader,
  BlackSpinner
} from './styled';

const EMPTY_COMPOSE = {
  visible: false,
  method: 'email',
  recipients: [],
  eventKey: null,
  eventName: null
};

/**
 * Outreach page — event-based communication with high-risk members.
 *
 * "By Event" (primary): pick an event, review its high-risk members, and email
 * or text them; contacted/responded status is tracked per member.
 * "Members" (secondary): a flat, filterable list of all high-risk members.
 *
 * SMS replies flow into the Inbox; this page surfaces contacted/responded
 * badges so staff know who was reached and who replied.
 */
const CaseManagement = observer(() => {
  const [activeTab, setActiveTab] = useState('queue');
  const [queueCount, setQueueCount] = useState(null);
  const { setPageHeader } = usePageHeader();

  // Events tab
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerReloadKey, setDrawerReloadKey] = useState(0);

  // Members tab
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [respondedFilter, setRespondedFilter] = useState('all');
  const [memberSelection, setMemberSelection] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(20);
  // Activity tab view: every platform email, or the contacted-members list.
  const [activityView, setActivityView] = useState('emails');

  // Compose modal (shared by events drawer and members tab)
  const [compose, setCompose] = useState(EMPTY_COMPOSE);

  // Member email viewer (Activity tab)
  const [viewMember, setViewMember] = useState(null);

  useEffect(() => {
    setPageHeader({
      title: 'Outreach',
      countIcon: <FontAwesomeIcon icon={faBullhorn} />
    });
  }, [setPageHeader]);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await caseService.getOutreachEvents();
      setEvents(res.data?.events || []);
    } catch (err) {
      message.error('Failed to load outreach events');
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const loadActivity = useCallback(async () => {
    setMembersLoading(true);
    try {
      const params = {
        risk: 'all',
        contacted: 'true',
        page: activityPage,
        pageSize: activityPageSize
      };
      if (respondedFilter === 'responded') params.responded = 'true';
      else if (respondedFilter === 'not-responded') params.responded = 'false';
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await caseService.getEventOutreachMembers('__all__', params);
      setMembers(res.data?.members || []);
      setActivityTotal(res.data?.total || 0);
      setMemberSelection([]);
    } catch (err) {
      message.error('Failed to load outreach activity');
    } finally {
      setMembersLoading(false);
    }
  }, [respondedFilter, debouncedSearch, activityPage, activityPageSize]);

  // Debounce the search box and reset to the first page on a new query/filter.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(memberSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [memberSearch]);

  useEffect(() => {
    setActivityPage(1);
  }, [debouncedSearch, respondedFilter]);

  useEffect(() => {
    if (activeTab === 'events') loadEvents();
  }, [activeTab, loadEvents]);

  useEffect(() => {
    if (activeTab === 'activity' && activityView === 'members') loadActivity();
  }, [activeTab, activityView, loadActivity]);

  // Summary stats across all events
  const summary = useMemo(() => {
    return events.reduce(
      (acc, e) => {
        acc.highRisk += e.highRiskCount || 0;
        acc.contacted += e.contactedCount || 0;
        acc.responded += e.respondedCount || 0;
        return acc;
      },
      { highRisk: 0, contacted: 0, responded: 0 }
    );
  }, [events]);

  // Members are searched/filtered/paginated server-side (see loadActivity).
  const filteredMembers = members;

  const openCompose = (method, recipients, eventKey, eventName) => {
    setCompose({ visible: true, method, recipients, eventKey, eventName });
  };

  const handleSent = () => {
    // Refresh whichever surfaces are showing outreach status.
    loadEvents();
    if (activeTab === 'activity' && activityView === 'members') loadActivity();
    if (drawerVisible) setDrawerReloadKey((k) => k + 1);
  };

  const openEventDrawer = (event) => {
    setSelectedEvent(event);
    setDrawerVisible(true);
  };

  const eventColumns = [
    {
      title: 'Event',
      key: 'event',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{record.eventName}</div>
          {record.eventLocation && (
            <div style={{ color: COLORS.textMedium, fontSize: 12 }}>
              <FontAwesomeIcon icon={faLocationDot} /> {record.eventLocation}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Date',
      key: 'date',
      width: 140,
      render: (_, record) =>
        record.eventDate ? (
          <span>
            <FontAwesomeIcon icon={faCalendar} style={{ color: COLORS.textMedium, marginRight: 6 }} />
            {new Date(record.eventDate).toLocaleDateString()}
          </span>
        ) : (
          <span style={{ color: COLORS.textMuted }}>—</span>
        ),
      sorter: (a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0)
    },
    {
      title: 'Members',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 100,
      align: 'center',
      render: (v) => (
        <span style={{ fontWeight: 600, color: COLORS.textDark }}>
          <FontAwesomeIcon icon={faUsers} style={{ color: COLORS.textMuted, marginRight: 6 }} />
          {v ?? 0}
        </span>
      ),
      sorter: (a, b) => (a.memberCount || 0) - (b.memberCount || 0)
    },
    {
      title: 'High Risk',
      dataIndex: 'highRiskCount',
      key: 'highRiskCount',
      width: 110,
      align: 'center',
      render: (v) => <Tag color="orange">{v}</Tag>,
      sorter: (a, b) => a.highRiskCount - b.highRiskCount
    },
    {
      title: 'Contacted',
      key: 'contacted',
      width: 200,
      render: (_, record) => {
        if (record.contactedCount == null) {
          return <BlackSpinner style={{ fontSize: 14 }} />;
        }
        const denom = record.memberCount || 0;
        const pct = denom
          ? Math.round((record.contactedCount / denom) * 100)
          : 0;
        return (
          <Progress
            percent={pct}
            size="small"
            strokeColor={COLORS.$primaryBlue}
            format={() => `${record.contactedCount}/${denom}`}
          />
        );
      }
    },
    {
      title: 'Opened',
      dataIndex: 'openedCount',
      key: 'openedCount',
      width: 110,
      align: 'center',
      render: (v) =>
        v == null ? (
          <BlackSpinner style={{ fontSize: 14 }} />
        ) : v > 0 ? (
          <Tag color="blue" icon={<FontAwesomeIcon icon={faEnvelope} />}>
            {v}
          </Tag>
        ) : (
          <span style={{ color: COLORS.textMuted }}>0</span>
        )
    },
    {
      title: 'Responded',
      dataIndex: 'respondedCount',
      key: 'respondedCount',
      width: 120,
      align: 'center',
      render: (v) =>
        v == null ? (
          <BlackSpinner style={{ fontSize: 14 }} />
        ) : v > 0 ? (
          <Tag color="green" icon={<FontAwesomeIcon icon={faCircleCheck} />}>
            {v}
          </Tag>
        ) : (
          <span style={{ color: COLORS.textMuted }}>0</span>
        )
    },
    {
      title: '',
      key: 'go',
      width: 50,
      align: 'center',
      render: () => <FontAwesomeIcon icon={faChevronRight} style={{ color: COLORS.textMuted }} />
    }
  ];

  const renderEventsTab = () => (
    <div>
      <StatsRow gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <OutreachStatCard $accent="#2E8B8B">
            <div className="stat-icon"><FontAwesomeIcon icon={faUsers} /></div>
            <div className="stat-content">
              <div className="stat-value">{summary.highRisk}</div>
              <div className="stat-label">High-Risk Members</div>
            </div>
          </OutreachStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <OutreachStatCard $accent={COLORS.$primaryBlue}>
            <div className="stat-icon"><FontAwesomeIcon icon={faEnvelope} /></div>
            <div className="stat-content">
              <div className="stat-value">{summary.contacted}</div>
              <div className="stat-label">Contacted</div>
            </div>
          </OutreachStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <OutreachStatCard $accent="#52c41a">
            <div className="stat-icon"><FontAwesomeIcon icon={faCircleCheck} /></div>
            <div className="stat-content">
              <div className="stat-value">{summary.responded}</div>
              <div className="stat-label">Responded</div>
            </div>
          </OutreachStatCard>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <OutreachStatCard $accent="#1a1a2e">
            <div className="stat-icon"><FontAwesomeIcon icon={faCalendar} /></div>
            <div className="stat-content">
              <div className="stat-value">{events.length}</div>
              <div className="stat-label">Events</div>
            </div>
          </OutreachStatCard>
        </Col>
      </StatsRow>

      {eventsLoading && events.length === 0 ? (
        <OutreachTableSkeleton columns={eventColumns} rows={6} scroll={{ x: 800 }} />
      ) : (
        <CaseTable
          columns={eventColumns}
          dataSource={events}
          loading={{ spinning: eventsLoading, indicator: <OutreachHeartLoader /> }}
          rowKey="eventKey"
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: () => openEventDrawer(record)
          })}
          locale={{ emptyText: 'No events with screened members yet' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`
          }}
          className="event-management-pagination"
          scroll={{ x: 800 }}
        />
      )}
    </div>
  );

  const selectedFlatMembers = filteredMembers.filter((m) => memberSelection.includes(m.patientId));
  // Email/text the checked members, or the members loaded on the current page
  // when nothing is checked (the list is server-paginated, so "all" here means
  // this page). Sends are capped at 100 recipients regardless.
  const memberTargets = memberSelection.length > 0 ? selectedFlatMembers : filteredMembers;
  const memberActionLabel = memberSelection.length > 0
    ? `${memberSelection.length} selected`
    : `page (${filteredMembers.length})`;

  const renderActivityTab = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={activityView}
          onChange={setActivityView}
          options={[
            { label: 'All emails', value: 'emails' },
            { label: 'Contacted members', value: 'members' }
          ]}
        />
      </div>
      {activityView === 'emails' ? (
        <EmailLogTable
          onRowClick={(record) => setViewMember({ patientId: record.patientId, email: record.to, name: record.to })}
        />
      ) : (
        renderContactedMembers()
      )}
    </div>
  );

  const renderContactedMembers = () => (
    <div>
      <FilterBar>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search contacted members by name, phone, or email..."
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: COLORS.textMuted }} />}
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              allowClear
              style={{ height: 40, borderRadius: 8 }}
            />
          </Col>
          <Col>
            <Segmented
              value={respondedFilter}
              onChange={setRespondedFilter}
              options={[
                { label: 'All', value: 'all' },
                { label: 'Opened', value: 'responded' },
                { label: 'Not opened', value: 'not-responded' }
              ]}
            />
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<FontAwesomeIcon icon={faEnvelope} />}
                disabled={memberTargets.length === 0}
                onClick={() => openCompose('email', memberTargets, null, null)}
              >
                Email ({memberActionLabel})
              </Button>
              <Button
                icon={<FontAwesomeIcon icon={faCommentSms} />}
                disabled={memberTargets.length === 0}
                onClick={() => openCompose('text', memberTargets, null, null)}
              >
                Text ({memberActionLabel})
              </Button>
            </Space>
          </Col>
        </Row>
      </FilterBar>

      <OutreachMembersTable
        members={filteredMembers}
        loading={membersLoading}
        variant="activity"
        selectedRowKeys={memberSelection}
        onSelectionChange={setMemberSelection}
        onEmail={(recs) => openCompose('email', recs, null, null)}
        onText={(recs) => openCompose('text', recs, null, null)}
        onRowClick={(record) => setViewMember(record)}
        pagination={{
          current: activityPage,
          pageSize: activityPageSize,
          total: activityTotal,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contacted members`,
          onChange: (page, size) => {
            setActivityPage(page);
            setActivityPageSize(size);
          }
        }}
        emptyText="No outreach sent yet — email or text members from an event to see activity here"
      />
    </div>
  );

  return (
    <>
      <PageContainer>
        <StyledTabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'queue',
              label: (
                <Space>
                  <FontAwesomeIcon icon={faPhoneVolume} />
                  Case Management
                  {queueCount != null && (
                    <TabBadge color={activeTab === 'queue' ? '#1890ff' : '#8c8c8c'}>
                      {queueCount}
                    </TabBadge>
                  )}
                </Space>
              )
            },
            {
              key: 'events',
              label: (
                <Space>
                  <FontAwesomeIcon icon={faCalendar} />
                  By Event
                  <TabBadge color={activeTab === 'events' ? '#1890ff' : '#8c8c8c'}>
                    {events.length}
                  </TabBadge>
                </Space>
              )
            },
            {
              key: 'activity',
              label: (
                <Space>
                  <FontAwesomeIcon icon={faClockRotateLeft} />
                  Activity
                </Space>
              )
            }
          ]}
        />
        <TabsBorderDiv />

        {activeTab === 'queue' && <CallQueueTab onCountChange={setQueueCount} />}
        {activeTab === 'events' && renderEventsTab()}
        {activeTab === 'activity' && renderActivityTab()}
      </PageContainer>

      <EventOutreachDrawer
        visible={drawerVisible}
        event={selectedEvent}
        reloadKey={drawerReloadKey}
        onClose={() => setDrawerVisible(false)}
        onCompose={openCompose}
      />

      <ComposeOutreachModal
        visible={compose.visible}
        method={compose.method}
        recipients={compose.recipients}
        eventKey={compose.eventKey}
        eventName={compose.eventName}
        onClose={() => setCompose(EMPTY_COMPOSE)}
        onSent={handleSent}
      />

      <Drawer
        open={!!viewMember}
        width={640}
        title={viewMember?.name || 'Member'}
        onClose={() => setViewMember(null)}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        {viewMember && (
          <PatientEmailPanel
            patientId={viewMember.patientId}
            name={viewMember.name}
            email={viewMember.email}
            phone={viewMember.phone}
          />
        )}
      </Drawer>
    </>
  );
});

export default CaseManagement;
